import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from 'app/user';
import { postSubscribe, getSubscription } from 'utils/api';

const PLAN_INFO = {
  basic: { name: 'BASIC', price: 9900 },
  premium: { name: 'PREMIUM', price: 19900 },
};

const Subscribe = () => {
  const { plan } = useParams();
  const navigate = useNavigate();
  const { updatePlan } = useUser();

  const [cardNo, setCardNo] = useState('');
  const [expYear, setExpYear] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [idNo, setIdNo] = useState('');
  const [cardPw, setCardPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFirstTime, setIsFirstTime] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await getSubscription();
        if (res.statusCode === 200 && res.results) {
          setIsFirstTime(res.results.isFirstTime === true);
        }
      } catch {
        // ignore
      }
    };
    fetchStatus();
  }, []);

  const planInfo = PLAN_INFO[plan];

  if (!planInfo) {
    return (
      <Container>
        <Title>유효하지 않은 플랜입니다.</Title>
      </Container>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const rawCardNo = cardNo.replace(/\s/g, '');
    if (rawCardNo.length < 15) {
      setError('카드번호를 정확히 입력해주세요.');
      return;
    }
    if (!expMonth || !expYear) {
      setError('유효기간을 입력해주세요.');
      return;
    }
    if (idNo.length < 6) {
      setError('생년월일 6자리를 입력해주세요.');
      return;
    }
    if (cardPw.length < 2) {
      setError('카드 비밀번호 앞 2자리를 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      const result = await postSubscribe({
        plan,
        cardNo: cardNo.replace(/\s/g, ''),
        expYear,
        expMonth,
        idNo,
        cardPw,
      });

      if (result.statusCode === 200) {
        updatePlan(plan);
        if (result.results?.legacyNotice) {
          alert(result.results.legacyNotice);
        }
        if (result.results?.firstTimeBonus) {
          alert(
            `${planInfo.name} 구독이 시작되었습니다! 첫 구독 1+1 혜택으로 2개월 동안 이용하실 수 있습니다.`,
          );
        } else {
          alert(`${planInfo.name} 구독이 시작되었습니다!`);
        }
        navigate('/analysis/estate');
      } else {
        const msg = result.message;
        if (Array.isArray(msg)) {
          setError('입력 정보를 다시 확인해주세요.');
        } else {
          setError(msg || '결제에 실패했습니다.');
        }
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      if (Array.isArray(msg)) {
        setError('입력 정보를 다시 확인해주세요.');
      } else {
        setError(msg || '결제 처리 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Title>{planInfo.name} 구독</Title>
        <PriceDisplay>월 {planInfo.price.toLocaleString()}원</PriceDisplay>
        {isFirstTime && (
          <FirstTimeBanner>
            <strong>첫 구독 1+1 이벤트</strong>
            <br />
            {planInfo.price.toLocaleString()}원 결제 → 2개월({planInfo.name})
            이용 가능
          </FirstTimeBanner>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>카드번호</Label>
            <Input
              type="text"
              placeholder="0000 0000 0000 0000"
              maxLength={19}
              value={cardNo}
              onChange={(e) => {
                const v = e.target.value
                  .replace(/\D/g, '')
                  .replace(/(.{4})/g, '$1 ')
                  .trim();
                setCardNo(v);
              }}
            />
          </FormGroup>

          <Row>
            <FormGroup>
              <Label>유효기간 (월)</Label>
              <Input
                type="text"
                placeholder="MM"
                maxLength={2}
                value={expMonth}
                onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, ''))}
              />
            </FormGroup>
            <FormGroup>
              <Label>유효기간 (년)</Label>
              <Input
                type="text"
                placeholder="YY"
                maxLength={2}
                value={expYear}
                onChange={(e) => setExpYear(e.target.value.replace(/\D/g, ''))}
              />
            </FormGroup>
          </Row>

          <Row>
            <FormGroup>
              <Label>생년월일 (6자리)</Label>
              <Input
                type="text"
                placeholder="YYMMDD"
                maxLength={6}
                value={idNo}
                onChange={(e) => setIdNo(e.target.value.replace(/\D/g, ''))}
              />
            </FormGroup>
            <FormGroup>
              <Label>카드 비밀번호 (앞 2자리)</Label>
              <Input
                type="password"
                placeholder="**"
                maxLength={2}
                value={cardPw}
                onChange={(e) => setCardPw(e.target.value.replace(/\D/g, ''))}
              />
            </FormGroup>
          </Row>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <SubmitButton type="submit" disabled={loading}>
            {loading
              ? '처리 중...'
              : `월 ${planInfo.price.toLocaleString()}원 결제하기`}
          </SubmitButton>

          <CancelLink onClick={() => navigate('/pricing')}>취소</CancelLink>
        </Form>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #232d42;
  text-align: center;
  margin-bottom: 8px;
`;

const PriceDisplay = styled.div`
  text-align: center;
  font-size: 18px;
  color: #644ded;
  font-weight: 600;
  margin-bottom: 16px;
`;

const FirstTimeBanner = styled.div`
  background: linear-gradient(135deg, #fff0f6 0%, #ffe3ee 100%);
  border: 1px solid #ff6b9a;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 24px;
  font-size: 13px;
  color: #c2185b;
  text-align: center;
  line-height: 1.6;

  strong {
    font-weight: 700;
    color: #ad1457;
    font-size: 14px;
  }
`;

const Form = styled.form``;

const FormGroup = styled.div`
  margin-bottom: 16px;
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e4e4e4;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #644ded;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
`;

const ErrorMsg = styled.div`
  color: #e53e3e;
  font-size: 13px;
  margin-bottom: 16px;
  text-align: center;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 0;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background-color: ${({ disabled }) => (disabled ? '#a3aed0' : '#644ded')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-top: 8px;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #5338c9;
  }
`;

const CancelLink = styled.div`
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #8f9bba;
  cursor: pointer;

  &:hover {
    color: #644ded;
  }
`;

export default Subscribe;
